import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import { ProduktDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/produkt-dto';
import { MatRadioChange } from '@angular/material/radio';
import { KundeDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/kunde-dto';
import { DriftformDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/driftform-dto';
import { FabrikatService, InstallatoerService, ModelService, ServiceAftaleService } from 'src/app/api/services';
import { FabrikatDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/fabrikat-dto';
import { AnlaegsKomponentComponent } from '../../anlaegs-komponent/anlaegs-komponent.component';
import { MatDialog } from '@angular/material/dialog';

import moment from 'moment';
import { ConfirmDialogComponent } from 'src/app/components/shared/confirm-dialog/confirm-dialog.component';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { EffektService } from 'src/app/services/aftale-helper.service';
import { KomponentTypeDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/komponent-type-dto';
import { AnlaegKomponentDialogData, DialogType } from '../../../models';
import { EnvironmentLoaderService } from 'src/app/services/environment-loader.service';
import { ProduktEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/produkt-enum';
import { InstallatoerDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/installatoer-dto';
import { StamdataStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/stamdata-status-enum';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ReplaySubject, Subject } from 'rxjs';
import { ModelDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/model-dto';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-produkt-form',
  templateUrl: './produkt-form.component.html',
  styleUrls: ['./produkt-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProduktFormComponent implements OnInit, OnDestroy {
  driftformer: DriftformDto[];
  installatoerer: InstallatoerDto[];
  isStorBraender: boolean = false;
  komponentTypeMap: Map<number, string>;
  komponentTyper: KomponentTypeDto[];
  fabrikatWarning: boolean = false;
  modelWarning: boolean = false;

  @Input() form: UntypedFormGroup;
  @Input() products: ProduktDto[];
  @Input() fabrikater: FabrikatDto[];
  @Input() modeller: ModelDto[];
  @Input() kunde: KundeDto;
  //@Input() selectedProduct: ProduktDto;
  @Output() productChange: EventEmitter<ProduktDto> = new EventEmitter();

  selectFabrikat$: ReplaySubject<string> = new ReplaySubject();
  selectModel$: ReplaySubject<string> = new ReplaySubject();

  canWrite: boolean = false;

  unsubscribe$ = new Subject<void>();

  private readonly minEffectForStorBraender = 135;

  get produktId() {
    return this.form.get('produktId');
  }

  get komponenter() {
    return this.form.get('komponenter') as UntypedFormArray;
  }

  get komponenterControls(): any[] {
    return this.komponenter.controls as any[];
  }

  get fabrikatId() {
    return this.form.get('fabrikatId');
  }

  get modelId() {
    return this.form.get('modelId');
  }

  get installatoerId() {
    return this.form.get('installatoerId');
  }

  get alternativModel() {
    return this.form.get('alternativModel');
  }

  get alternativInstallatoer() {
    return this.form.get('alternativInstallatoer');
  }

  get storbraender() {
    return this.form.get('storbraender');
  }

  get effekt() {
    return this.form.get('effekt');
  }

  get maxEffekt() {
    return this.form.get('maxEffekt');
  }

  get installationDate() {
    return this.form.get('installationDate');
  }

  get serienummer() {
    return this.form.get('serienummer');
  }

  get garantiPeriode() {
    return this.form.get('garantiPeriode');
  }

  get driftformId() {
    return this.form.get('driftformId');
  }

  get erGas() {
    return this.produktId.value == ProduktEnum.Gas; // 1
  }

  get erOlie() {
    return this.produktId.value == ProduktEnum.Olie; // 2
  }

  get erVp() {
    return this.produktId.value == ProduktEnum.Varmepumpe; // 3
  }

  constructor(
    private serviceAftaleService: ServiceAftaleService,
    public dialog: MatDialog,
    private errorHandler: ErrorHandlerService,
    private effektService: EffektService,
    private env: EnvironmentLoaderService,
    private installatoerService: InstallatoerService,
    private auth: AuthService,
  ) {
    this.canWrite = this.auth.canWrite;
  }

  ngOnInit(): void {
    this.installatoerService.getInstallatoerer({ caller: EGIS_SERVICE_CALLER, body: { searchQuery: '' } }).subscribe(
      (data) => (this.installatoerer = data),
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
      },
    );

    this.serviceAftaleService.getAvailableDriftformer({ caller: EGIS_SERVICE_CALLER }).subscribe(
      (data) => {
        this.driftformer = data;
      },
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
      },
    );
    this.serviceAftaleService.getAvailableKomponentTyper({ caller: EGIS_SERVICE_CALLER }).subscribe(
      (data) => {
        this.komponentTyper = data;
        this.komponentTypeMap = new Map<number, string>(data.map((x) => [x.id, x.text] as [number, string]));
      },
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
      },
    );

    this.fabrikatId.setValidators(Validators.required);

    this.effekt.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      if (this.isOlieOrGas()) {
        const effekt = this.effektService.getEffekt(val);

        if (effekt.minEffekt >= this.minEffectForStorBraender || effekt.maxEffekt >= this.minEffectForStorBraender) {
          this.storbraender.setValue(true);
          this.onStorbraenderChange();
        }
      }
    });

    this.installationDate.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      if (!val) {
        this.garantiPeriode.disable();
        this.garantiPeriode.setValue(null);
      } else {
        this.garantiPeriode.enable();
      }
    });

    this.storbraender.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      this.isStorBraender = val;
      //Olie and gas
      if (val && this.isOlieOrGas()) {
        this.driftformId.setValidators(Validators.required);
      } else {
        this.driftformId.clearValidators();
        this.driftformId.setValue(null);
      }
      this.driftformId.updateValueAndValidity();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  addKomponent() {
    let data: AnlaegKomponentDialogData = {
      komponent: null,
      editMode: false,
      minInstallDate: this.installationDate.value,
      komponentTyper: this.komponentTyper,
      komponenter: this.komponenter.controls.map((x) => ({ serienummer: x.get('serienummer').value, model: x.get('model').value })),
    };

    const dialogRef = this.dialog.open(AnlaegsKomponentComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.komponenter.push(result);
      }
    });
  }

  editKomponent(komponent: any) {
    let data: AnlaegKomponentDialogData = {
      komponent: komponent,
      editMode: true,
      minInstallDate: this.installationDate.value,
      komponentTyper: this.komponentTyper,
    };

    const dialogRef = this.dialog.open(AnlaegsKomponentComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const index: number = this.komponenter.controls.indexOf(komponent);
        if (index !== -1) {
          this.komponenter.at(index).patchValue(result, { onlySelf: true, emitEvent: true });
        }
      }
    });
  }

  deleteKomponent(komponent: any) {
    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Er du sikker?',
        body: 'Er du sikker på at du vil slette kompenenten med serienr: ' + komponent.controls.serienummer.value + '?',
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        const index: number = this.komponenter.controls.indexOf(komponent);
        if (index !== -1) {
          this.komponenter.removeAt(index);
        }
      }
    });
  }

  onFabrikatChange(fabrikatDto: FabrikatDto) {
    this.fabrikatWarning = false;
    if (fabrikatDto) {
      this.fabrikatId.setValidators(Validators.required);
      this.fabrikatId.setValue(fabrikatDto.id);
      if (fabrikatDto.fabrikatstatus == StamdataStatusEnum.Maybe) this.fabrikatWarning = true;
    } else {
      this.fabrikatId.setValue(null);
      this.fabrikatId.clearValidators();
      this.fabrikatId.reset();
      this.modelId.setValue(null);
      this.modelId.clearValidators();
      this.modelId.reset();
    }
  }

  onModelChange(model: string) {
    this.modelWarning = false;
    let dto = this.modeller.find((m) => m.name == model);
    if (dto) {
      this.modelId.setValue(dto.id);
      this.alternativModel.reset();
    } else {
      this.alternativModel.setValue(model);
      this.modelId.reset();
    }
  }

  onInstallatoerChange(installatoer: string) {
    let dto = this.installatoerer.find((x) => x.name == installatoer);
    if (dto) {
      this.installatoerId.setValidators(Validators.required);
      this.installatoerId.setValue(dto.id);
      this.alternativInstallatoer.clearValidators();
      this.alternativInstallatoer.reset();
    } else {
      this.alternativInstallatoer.setValidators(Validators.required);
      this.alternativInstallatoer.setValue(installatoer);
      this.installatoerId.clearValidators();
      this.installatoerId.reset();
    }
  }

  onProductChange(event: MatRadioChange): void {
    this.selectFabrikat$.next(null);
    this.fabrikatWarning = false;
    this.productChange.emit(event.value);
  }

  onStorbraenderChange(): void {
    this.productChange.emit();
  }

  getGarantiEndDate(instaDate: Date, garanti: number): Date {
    return moment(instaDate).add(garanti, 'month').subtract(1, 'day').toDate();
  }

  private isOlieOrGas(): boolean {
    return this.produktId.value == ProduktEnum.Gas || this.produktId.value == ProduktEnum.Olie;
  }
}
